import { useState, useEffect, useMemo } from 'react';
import { Plus, AlertCircle, CheckCircle, Clock, Building2, GitBranch, ChevronLeft, ChevronRight, Layers3 } from 'lucide-react';
import Select from 'react-select';
import api, { organizationsAPI, riskQueueAPI } from '../../services/api';
import toast from 'react-hot-toast';
import AssignCourseModal from '../../components/lms/AssignCourseModal';
import AssignLearningPathModal from '../../components/lms/AssignLearningPathModal';
import RolloutCourseModal from '../../components/lms/RolloutCourseModal';
import useAuthStore from '../../context/authStore';
import { deadlineRequestsAPI } from '../../services/api';
import { normalizeRole } from '../../utils/roleNormalization';
import { publishNotificationState } from '../../hooks/useNavigationNotifications';

const labelize = (value) => String(value || '').replace(/_/g, ' ');

function SummaryCard({ title, value, tone = 'slate' }) {
  const toneClasses = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
  };
  return (
    <div className={`rounded-2xl border p-4 ${toneClasses[tone] || toneClasses.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{title}</p>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

function GuidancePanel({ title, reasons = [], action, tone = 'slate' }) {
  if (!reasons.length && !action) return null;

  const toneClasses = {
    slate: 'border-slate-200 bg-slate-50 text-slate-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-900',
  };

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${toneClasses[tone] || toneClasses.slate}`}>
      {title && <p className="font-semibold uppercase tracking-wide">{title}</p>}
      {reasons.length > 0 && (
        <div className="mt-1 space-y-1">
          {reasons.slice(0, 2).map((reason, index) => (
            <p key={`${title || 'guidance'}-${index}`}>{reason}</p>
          ))}
        </div>
      )}
      {action && (
        <p className="mt-2 font-medium">
          Next: {action}
        </p>
      )}
    </div>
  );
}

export default function TrainingManagementPage() {
  const { user } = useAuthStore();
  const organizationId = user?.organizationId?._id || user?.organizationId;
  const isOrgLevelTrainingManager = ['org_admin', 'org_team'].includes(user?.role);
  const isFacilityLevelTrainingManager = ['facility_admin', 'facility_manager'].includes(user?.role);
  const isFacilityManager = user?.role === 'facility_manager';
  const [courses, setCourses] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const teamMemberCurrentUser = teamMembers.find(
    (member) => String(member?._id) === String(user?._id),
  );
  const authFacilityIds = Array.isArray(user?.clientStaffProfile?.facilityIds)
    ? user.clientStaffProfile.facilityIds.map((id) => String(id))
    : [];
  const apiFacilityIds = Array.isArray(teamMemberCurrentUser?.clientStaffProfile?.facilityIds)
    ? teamMemberCurrentUser.clientStaffProfile.facilityIds.map((id) => String(id))
    : [];
  const userFacilityIds = authFacilityIds.length > 0 ? authFacilityIds : apiFacilityIds;
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('');
  const [showRolloutModal, setShowRolloutModal] = useState(false);
  const [rolloutCourse, setRolloutCourse] = useState(null);
  const [learningPaths, setLearningPaths] = useState([]);
  const [showAssignPathModal, setShowAssignPathModal] = useState(false);
  const [selectedLearningPath, setSelectedLearningPath] = useState(null);
  const [deadlineRequests, setDeadlineRequests] = useState([]);
  const [riskQueue, setRiskQueue] = useState([]);
  const [rolloutPlans, setRolloutPlans] = useState([]);
  const [orgLearningPackages, setOrgLearningPackages] = useState([]);
  const [organizationFacilities, setOrganizationFacilities] = useState([]);
  const [selectedFacilityFilter, setSelectedFacilityFilter] = useState('all');
  const [coursePage, setCoursePage] = useState(1);
  const [pathPage, setPathPage] = useState(1);
  const [deadlinePage, setDeadlinePage] = useState(1);
  const [rolloutPlanPage, setRolloutPlanPage] = useState(1);
  const [rolloutRowPage, setRolloutRowPage] = useState(1);
  const [hqAssignmentPage, setHqAssignmentPage] = useState(1);
  const [facilityAssignmentPage, setFacilityAssignmentPage] = useState(1);
  const [riskPage, setRiskPage] = useState(1);
  const [riskTypeFilter, setRiskTypeFilter] = useState('all');
  const [riskSeverityFilter, setRiskSeverityFilter] = useState('all');
  const [riskStatusFilter, setRiskStatusFilter] = useState('all');
  const [selectedRiskKeys, setSelectedRiskKeys] = useState([]);
  
  // manual reminder trigger (client admin)
  const runReminders = async () => {
    try {
      const res = await api.post('/lms/reminders/run');
      const { dueCount, overdueCount } = res.data || {};
      toast.success(`Reminders queued: ${dueCount || 0} due, ${overdueCount || 0} overdue`);
    } catch (err) {
      console.error('run reminders error', err);
      toast.error('Failed to run reminders');
    }
  };
  
  useEffect(() => {
    if (organizationId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    setCoursePage(1);
    setPathPage(1);
    setDeadlinePage(1);
    setRolloutPlanPage(1);
    setRolloutRowPage(1);
    setHqAssignmentPage(1);
    setFacilityAssignmentPage(1);
    setRiskPage(1);
    setSelectedRiskKeys([]);
  }, [searchTerm, statusFilter, courseFilter, organizationId, selectedFacilityFilter]);

  const fetchData = async () => {
    try {
      if (!organizationId) return;
      setLoading(true);
      const [
        coursesRes,
        assignmentsRes,
        membersRes,
        learningPathsRes,
        learningPackagesRes,
        deadlineRequestsRes,
        rolloutPlansRes,
        facilitiesRes,
        riskQueueRes,
      ] = await Promise.all([
        api.get('/lms/courses?status=published&limit=100'),
        api.get(`/lms/enrollments/org/${organizationId}?limit=1000`),
        organizationsAPI.getUsers(organizationId),
        api.get(`/lms/reports/org/${organizationId}/learning-paths`),
        isOrgLevelTrainingManager ? api.get(`/lms/reports/org/${organizationId}/learning-packages`) : Promise.resolve({ data: {} }),
        deadlineRequestsAPI.getOrgSummary(organizationId, { status: 'all' }),
        api.get(`/lms/rollout/org/${organizationId}`),
        isOrgLevelTrainingManager ? organizationsAPI.getMyFacilities() : Promise.resolve({ data: [] }),
        riskQueueAPI.getOrg(organizationId),
      ]);

      setCourses(coursesRes.data?.courses || coursesRes.data || []);
      setAssignments(assignmentsRes.data?.enrollments || assignmentsRes.data || []);
      setTeamMembers(
        (membersRes.data?.users || membersRes.data || []).map((member) => ({
          ...member,
          role: normalizeRole(member.role),
        })),
      );
      setLearningPaths(learningPathsRes.data?.learningPaths || []);
      setOrgLearningPackages(learningPackagesRes.data?.learningPackages || []);
      setDeadlineRequests(deadlineRequestsRes.data?.requests || []);
      setRolloutPlans(rolloutPlansRes.data?.rollouts || []);
      setOrganizationFacilities(facilitiesRes.data?.facilities || facilitiesRes.data || []);
      setRiskQueue(riskQueueRes.data?.queue || []);
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignModal = (course) => {
    setSelectedCourse(course);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setSelectedCourse(null);
    setShowAssignModal(false);
    fetchData();
  };

  const handleOpenRolloutModal = (course) => {
    setRolloutCourse(course);
    setShowRolloutModal(true);
  };

  const handleCloseRolloutModal = () => {
    setRolloutCourse(null);
    setShowRolloutModal(false);
    fetchData();
  };

  const handleOpenAssignPathModal = (learningPath) => {
    setSelectedLearningPath(learningPath);
    setShowAssignPathModal(true);
  };

  const handleCloseAssignPathModal = () => {
    setSelectedLearningPath(null);
    setShowAssignPathModal(false);
    fetchData();
  };

  const handleRemoveAssignment = async (assignment) => {
    const learnerName = `${assignment.userId?.firstName || ''} ${assignment.userId?.lastName || ''}`.trim() || 'this learner';
    const confirmed = window.confirm(
      `Remove "${assignment.courseId?.title || 'this course'}" for ${learnerName}?`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/lms/enrollments/${assignment._id}`);
      toast.success('Assignment removed and seat released');
      fetchData();
    } catch (error) {
      console.error('Remove assignment error:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to remove assignment');
    }
  };

  const handleDeadlineRequestResponse = async (request, action) => {
    try {
      await deadlineRequestsAPI.respond(request.enrollmentId, request.requestId, { action });
      toast.success(action === 'approve' ? 'Deadline request approved' : 'Deadline request denied');
      publishNotificationState({ kind: 'refresh' });
      fetchData();
    } catch (error) {
      console.error('Respond deadline request error:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to update deadline request');
    }
  };

  const handleRiskQueueAction = async (item, actionType) => {
    try {
      let payload = { riskKey: item.key, actionType };

      if (actionType === 'mark_resolved') {
        const note = window.prompt('Add a resolution note');
        if (!note || !note.trim()) return;
        payload.note = note.trim();
      }

      if (actionType === 'escalate') {
        const note = window.prompt('Add a note for the facility admin (optional)') || '';
        payload.note = note.trim();
        payload.escalationTarget = 'facility_admin';
      }

      if (actionType === 'remove_from_path') {
        const confirmed = window.confirm(`Remove ${item.userName || 'this learner'} from ${item.learningPathTitle || 'this learning path'}?`);
        if (!confirmed) return;
      }

      if (actionType === 'reissue_certificate') {
        const confirmed = window.confirm(`Reissue the certificate for ${item.userName || 'this learner'}?`);
        if (!confirmed) return;
      }

      const response = await riskQueueAPI.act(organizationId, payload);
      toast.success(
        actionType === 'escalate'
          ? 'Flagged for facility admin review'
          : response.data?.message || response.message || 'Action completed',
      );
      fetchData();
    } catch (error) {
      console.error('Risk queue action error:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to perform action');
    }
  };

  const handleBulkRiskQueueAction = async (actionType) => {
    try {
      if (!selectedRiskKeys.length) return;
      let payload = { riskKeys: selectedRiskKeys, actionType };

      if (actionType === 'mark_resolved') {
        const note = window.prompt('Add a resolution note for the selected queue items');
        if (!note || !note.trim()) return;
        payload.note = note.trim();
      }

      if (actionType === 'escalate') {
        const note = window.prompt('Add a note for the facility admin (optional)') || '';
        payload.note = note.trim();
        payload.escalationTarget = 'facility_admin';
      }

      if (actionType === 'remove_from_path') {
        const confirmed = window.confirm(`Remove ${selectedRiskKeys.length} learner(s) from their learning path?`);
        if (!confirmed) return;
      }

      if (actionType === 'reissue_certificate') {
        const confirmed = window.confirm(`Reissue ${selectedRiskKeys.length} certificate(s)?`);
        if (!confirmed) return;
      }

      const response = await riskQueueAPI.bulkAct(organizationId, payload);
      toast.success(
        actionType === 'escalate'
          ? 'Selected items flagged for facility admin review'
          : response.data?.message || response.message || 'Bulk action completed',
      );
      setSelectedRiskKeys([]);
      fetchData();
    } catch (error) {
      console.error('Bulk risk queue action error:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to perform bulk action');
    }
  };

  const PAGE_SIZES = {
    courses: 6,
    learningPaths: 5,
    deadlineRequests: 6,
    rolloutPlans: 6,
    rolloutRows: 6,
    hqAssignments: 6,
    facilityAssignments: 8,
    riskQueue: 6,
  };

  const paginateItems = (items, page, pageSize) => {
    const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * pageSize;
    return {
      items: (items || []).slice(start, start + pageSize),
      page: safePage,
      totalPages,
      totalItems: items?.length || 0,
    };
  };

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAssignments = assignments
    .filter((a) => (courseFilter ? a.courseId._id === courseFilter : true))
    .filter((a) => {
      if (statusFilter === 'all') return true;
      return a.status === statusFilter;
    })
    .filter((a) => a.courseId.title.toLowerCase().includes(searchTerm.toLowerCase()));

  const teamMemberById = new Map(
    teamMembers.map((member) => [String(member?._id || ''), member]),
  );

  const resolveMemberProfile = (member) => {
    const directProfile = member?.clientStaffProfile;
    const fallbackProfile = teamMemberById.get(String(member?._id || ''))?.clientStaffProfile;
    return directProfile || fallbackProfile || null;
  };

  const getMemberFacilityIds = (member) =>
    Array.isArray(resolveMemberProfile(member)?.facilityIds)
      ? resolveMemberProfile(member).facilityIds.map((id) => String(id))
      : [];

  const getMemberFacilityNames = (member) => {
    const profile = resolveMemberProfile(member);
    if (Array.isArray(profile?.facilityNames) && profile.facilityNames.length > 0) {
      return profile.facilityNames;
    }
    const fallbackProfile = teamMemberById.get(String(member?._id || ''))?.clientStaffProfile;
    if (Array.isArray(fallbackProfile?.facilityNames) && fallbackProfile.facilityNames.length > 0) {
      return fallbackProfile.facilityNames;
    }
    return [];
  };

  const facilityFilterOptions = organizationFacilities
    .map((facility) => ({
      id: String(facility?._id || ''),
      name: facility?.name || 'Facility',
    }))
    .filter((facility) => facility.id);

  const selectedFacilityName =
    selectedFacilityFilter === 'all'
      ? null
      : facilityFilterOptions.find((facility) => facility.id === selectedFacilityFilter)?.name || null;

  const matchesSelectedFacility = (facilityIds = [], facilityNames = []) => {
    if (selectedFacilityFilter === 'all') return true;
    const normalizedIds = (facilityIds || []).map((id) => String(id));
    if (normalizedIds.includes(selectedFacilityFilter)) return true;
    if (selectedFacilityName && (facilityNames || []).includes(selectedFacilityName)) return true;
    return false;
  };

  const isHeadquartersMember = (member) =>
    ['org_admin', 'org_team'].includes(member?.role);

  const isMemberInUserFacilityScope = (member) => {
    if (!isFacilityLevelTrainingManager) return true;
    // If we couldn't resolve the admin's own facility IDs, the backend already
    // scoped the list to the org — don't filter out everyone.
    if (userFacilityIds.length === 0) return true;
    const memberFacilityIds = getMemberFacilityIds(member);
    // Members with no facility IDs set are visible to their admin
    if (memberFacilityIds.length === 0) return true;
    return memberFacilityIds.some((id) => userFacilityIds.includes(id));
  };

  const visibleAssignableTeamMembers = teamMembers.filter((member) => {
    if (isOrgLevelTrainingManager) {
      return ['org_admin', 'org_team'].includes(member?.role);
    }
    if (isFacilityLevelTrainingManager) {
      return (
        String(member?._id) === String(user?._id) ||
        isMemberInUserFacilityScope(member)
      );
    }
    return true;
  });

  const headquartersAssignments = filteredAssignments.filter((assignment) =>
    isHeadquartersMember(assignment.userId),
  );

  const facilityAssignments = filteredAssignments.filter((assignment) =>
    !isHeadquartersMember(assignment.userId) &&
    matchesSelectedFacility(
      getMemberFacilityIds(assignment.userId),
      getMemberFacilityNames(assignment.userId),
    ),
  );

  const getEnrollmentStats = (courseId) => {
    const courseAssignments = assignments.filter((a) => a.courseId._id === courseId);
    return {
      total: courseAssignments.length,
      started: courseAssignments.filter((a) => a.status === 'in_progress').length,
      completed: courseAssignments.filter((a) => a.status === 'completed').length,
      notStarted: courseAssignments.filter((a) => a.status === 'not_started').length,
    };
  };

  const getOrgCourseStats = (courseId) => {
    const courseAssignments = assignments.filter((a) => a.courseId._id === courseId);
    const hqCourseAssignments = courseAssignments.filter((assignment) => isHeadquartersMember(assignment.userId));
    const facilityNames = new Set();
    let rolloutExceptions = 0;
    courseAssignments
      .filter((assignment) => !isHeadquartersMember(assignment.userId))
      .forEach((assignment) => {
        const names = getMemberFacilityNames(assignment.userId);
        if (names.length === 0) {
          rolloutExceptions += 1;
          return;
        }
        names.forEach((name) => facilityNames.add(name));
      });

    return {
      facilityRolloutCount: facilityNames.size,
      hqAssigned: hqCourseAssignments.length,
      hqCompleted: hqCourseAssignments.filter((assignment) => assignment.status === 'completed').length,
      rolloutExceptions,
    };
  };

  const getFacilityRolloutRows = () => {
    const rows = new Map();

    facilityAssignments.forEach((assignment) => {
      const facilityNames = getMemberFacilityNames(assignment.userId);
      if (facilityNames.length === 0) {
        return;
      }

      facilityNames.forEach((facilityName) => {
        const key = `${assignment.courseId._id}:${facilityName}`;
        if (!rows.has(key)) {
          rows.set(key, {
            key,
            courseId: assignment.courseId._id,
            courseTitle: assignment.courseId.title,
            facilityName,
            assigned: 0,
            inProgress: 0,
            completed: 0,
            notStarted: 0,
          });
        }

        const row = rows.get(key);
        row.assigned += 1;
        if (assignment.status === 'completed') row.completed += 1;
        else if (assignment.status === 'in_progress') row.inProgress += 1;
        else if (assignment.status === 'not_started') row.notStarted += 1;
      });
    });

    return Array.from(rows.values()).sort((a, b) => {
      if (a.courseTitle === b.courseTitle) return a.facilityName.localeCompare(b.facilityName);
      return a.courseTitle.localeCompare(b.courseTitle);
    });
  };

  const facilityRolloutRows = getFacilityRolloutRows();
  const rolloutExceptionAssignments = facilityAssignments.filter((assignment) => getMemberFacilityNames(assignment.userId).length === 0);
  const visibleLearningPaths = learningPaths.filter((path) =>
    matchesSelectedFacility(path.facilityIds || [], path.facilityNames || []),
  );
  const rolloutPlanSummary = Array.from(
    rolloutPlans.reduce((acc, rollout) => {
      if (
        !matchesSelectedFacility(
          [String(rollout.facilityId?._id || rollout.facilityId || '')].filter(Boolean),
          [rollout.facilityId?.name].filter(Boolean),
        )
      ) {
        return acc;
      }
      const courseId = String(rollout.courseId?._id || rollout.courseId || '');
      const facilityName = rollout.facilityId?.name || 'Unnamed facility';
      const existing = acc.get(courseId) || {
        key: courseId || String(rollout._id),
        courseId,
        courseTitle: rollout.courseId?.title || 'Course',
        facilities: new Set(),
        latestRolledOutAt: null,
      };
      existing.facilities.add(facilityName);
      if (!existing.latestRolledOutAt || new Date(rollout.createdAt) > new Date(existing.latestRolledOutAt)) {
        existing.latestRolledOutAt = rollout.createdAt;
      }
      acc.set(courseId, existing);
      return acc;
    }, new Map()).values(),
  ).map((entry) => ({
    ...entry,
    facilityNames: Array.from(entry.facilities),
    facilityCount: entry.facilities.size,
  })).sort((a, b) => a.courseTitle.localeCompare(b.courseTitle));

  const orgPackageVisibilitySummary = useMemo(() => {
    const visiblePackages = orgLearningPackages.filter((item) =>
      matchesSelectedFacility(item.facilityIds || [], item.facilityNames || []),
    );
    const assignedLearners = visiblePackages.reduce((total, item) => total + Number(item.assignedLearners || 0), 0);
    const completedLearners = visiblePackages.reduce((total, item) => total + Number(item.completedLearners || 0), 0);
    const overdueLearners = visiblePackages.reduce((total, item) => total + Number(item.overdueLearners || 0), 0);
    const averageProgress = visiblePackages.length
      ? Math.round(
          visiblePackages.reduce((total, item) => total + Number(item.avgProgress || 0), 0) / visiblePackages.length,
        )
      : 0;

    return {
      visiblePackages,
      assignedLearners,
      completedLearners,
      overdueLearners,
      averageProgress,
    };
  }, [orgLearningPackages, selectedFacilityFilter, selectedFacilityName]);

  const pagedCourses = paginateItems(filteredCourses, coursePage, PAGE_SIZES.courses);
  const pagedLearningPaths = paginateItems(visibleLearningPaths, pathPage, PAGE_SIZES.learningPaths);
  const visibleDeadlineRequests = deadlineRequests.filter((request) =>
    matchesSelectedFacility(request.facilityIds || [], request.facilityNames || []),
  );
  const visibleRiskQueue = riskQueue.filter((item) =>
    item.riskType !== 'deadline_request_pending' &&
    matchesSelectedFacility(item.facilityIds || [], item.facilityNames || []),
  );
  const filteredRiskQueue = visibleRiskQueue.filter((item) => {
    if (riskTypeFilter !== 'all' && item.riskType !== riskTypeFilter) return false;
    if (riskSeverityFilter !== 'all' && item.severity !== riskSeverityFilter) return false;
    if (riskStatusFilter === 'escalated' && !item.isEscalated) return false;
    if (riskStatusFilter === 'active' && item.isEscalated) return false;
    return true;
  });
  const pagedRiskQueue = paginateItems(filteredRiskQueue, riskPage, PAGE_SIZES.riskQueue);
  const pagedDeadlineRequests = paginateItems(visibleDeadlineRequests, deadlinePage, PAGE_SIZES.deadlineRequests);
  const pagedRolloutPlans = paginateItems(rolloutPlanSummary, rolloutPlanPage, PAGE_SIZES.rolloutPlans);
  const pagedFacilityRolloutRows = paginateItems(facilityRolloutRows, rolloutRowPage, PAGE_SIZES.rolloutRows);
  const pagedHeadquartersAssignments = paginateItems(headquartersAssignments, hqAssignmentPage, PAGE_SIZES.hqAssignments);
  const pagedFacilityAssignments = paginateItems(filteredAssignments, facilityAssignmentPage, PAGE_SIZES.facilityAssignments);
  const orgExecutiveSummary = useMemo(() => {
    if (!isOrgLevelTrainingManager) return null;
    const total = filteredAssignments.length;
    const completed = filteredAssignments.filter((assignment) => assignment.status === 'completed').length;
    const overdue = filteredAssignments.filter((assignment) => assignment.status === 'overdue').length;
    const blockedPaths = visibleRiskQueue.filter((item) => item.riskType === 'blocked_learning_path').length;
    const certificateRisk = visibleRiskQueue.filter((item) => item.riskType === 'certificate_expiring' || item.riskType === 'certificate_expired').length;
    const activeFacilities = new Set(rolloutPlanSummary.flatMap((entry) => entry.facilityNames || [])).size;

    return {
      totalAssignments: total,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      overdueAssignments: overdue,
      blockedPaths,
      certificateRisk,
      activeFacilities,
    };
  }, [isOrgLevelTrainingManager, filteredAssignments, visibleRiskQueue, rolloutPlanSummary]);

  const facilityHeatmapRows = useMemo(() => {
    if (!isOrgLevelTrainingManager) return [];
    return facilityFilterOptions.map((facility) => {
      const scopedAssignments = assignments.filter((assignment) =>
        !isHeadquartersMember(assignment.userId) &&
        matchesSelectedFacility(
          getMemberFacilityIds(assignment.userId),
          getMemberFacilityNames(assignment.userId),
        ) &&
        (
          getMemberFacilityIds(assignment.userId).includes(facility.id) ||
          getMemberFacilityNames(assignment.userId).includes(facility.name)
        ),
      );
      const completed = scopedAssignments.filter((assignment) => assignment.status === 'completed').length;
      const overdue = scopedAssignments.filter((assignment) => assignment.status === 'overdue').length;
      const pendingRequests = visibleDeadlineRequests.filter((request) =>
        (request.facilityIds || []).map(String).includes(facility.id) ||
        (request.facilityNames || []).includes(facility.name)
      ).length;
      const activeRisks = visibleRiskQueue.filter((item) =>
        (item.facilityIds || []).map(String).includes(facility.id) ||
        (item.facilityNames || []).includes(facility.name)
      ).length;
      return {
        id: facility.id,
        name: facility.name,
        assigned: scopedAssignments.length,
        completionRate: scopedAssignments.length > 0 ? Math.round((completed / scopedAssignments.length) * 100) : 0,
        overdue,
        pendingRequests,
        activeRisks,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [
    isOrgLevelTrainingManager,
    facilityFilterOptions,
    assignments,
    visibleDeadlineRequests,
    visibleRiskQueue,
    selectedFacilityFilter,
  ]);

  const selectedFacilityViewLabel = useMemo(() => {
    if (!isOrgLevelTrainingManager) return null;
    if (selectedFacilityFilter === 'all') return 'all released facilities';
    const facility = facilityFilterOptions.find((option) => option.id === selectedFacilityFilter);
    return facility?.name || 'the selected facility';
  }, [isOrgLevelTrainingManager, selectedFacilityFilter, facilityFilterOptions]);

  const managementNarrative = useMemo(() => {
    const overdueAssignments = filteredAssignments.filter((assignment) => assignment.status === 'overdue').length;
    const blockedPathItems = visibleRiskQueue.filter((item) => item.riskType === 'blocked_learning_path').length;
    const expiringCertificates = visibleRiskQueue.filter((item) => item.riskType === 'certificate_expiring' || item.riskType === 'certificate_expired').length;
    const pendingExtensions = visibleDeadlineRequests.filter((request) => request.status === 'pending').length;

    if (isOrgLevelTrainingManager) {
      return {
        title: 'What headquarters should focus on',
        reasons: [
          `This view is a lightweight oversight snapshot for ${selectedFacilityViewLabel || 'the organization'}. Facility teams still own day-to-day learner follow-up.`,
          `${overdueAssignments} overdue assignment(s), ${blockedPathItems} blocked path item(s), and ${expiringCertificates} certificate risk item(s) currently need attention.`,
        ],
        action:
          pendingExtensions > 0
            ? `Use the deadline request summary to spot volume, then let facility admins handle the approvals.`
            : 'Use the heatmap and action queue to identify which facility needs follow-up first.',
        tone: overdueAssignments > 0 || blockedPathItems > 0 ? 'amber' : 'blue',
      };
    }

    return {
      title: 'What needs your attention',
      reasons: [
        `Only learners inside your facility scope appear here, so you do not need to sort through other facilities' work.`,
        `${overdueAssignments} overdue assignment(s), ${pendingExtensions} pending extension request(s), and ${blockedPathItems} blocked path item(s) are currently in your lane.`,
      ],
      action:
        pendingExtensions > 0
          ? 'Start with pending deadline requests, then handle overdue learners and blocked paths.'
          : 'Work the action queue first, then review assignment status for stalled learners.',
      tone: pendingExtensions > 0 || overdueAssignments > 0 ? 'amber' : 'emerald',
    };
  }, [
    filteredAssignments,
    isOrgLevelTrainingManager,
    selectedFacilityViewLabel,
    visibleDeadlineRequests,
    visibleRiskQueue,
  ]);

  const buildCourseGuidance = (course, orgStats, stats, assignmentBlocked, missingAccess, plannedSteps) => {
    if (assignmentBlocked) {
      return {
        tone: 'amber',
        reasons: [`Assignment is blocked until prerequisite access is restored for ${missingAccess.map((item) => item.title).join(', ')}.`],
        action: 'Restore prerequisite access first, then release or assign this course.',
      };
    }

    if (plannedSteps.length > 0) {
      return {
        tone: 'slate',
        reasons: [`This course is ready, but later planned steps are still not live: ${plannedSteps.map((item) => item.title).join(', ')}.`],
        action: isOrgLevelTrainingManager ? 'Release this course now and publish the remaining path steps later.' : 'You can assign this course now; later path steps will follow when released.',
      };
    }

    if (isOrgLevelTrainingManager) {
      const reasons = [];
      if (orgStats.facilityRolloutCount > 0) {
        reasons.push(`Headquarters has already released this course to ${orgStats.facilityRolloutCount} facility view(s).`);
      } else {
        reasons.push('This course is not released to any facility yet, so only headquarters assignment will create learner activity.');
      }
      if (orgStats.rolloutExceptions > 0) {
        reasons.push(`${orgStats.rolloutExceptions} facility learner assignment(s) are still excluded because the learner profile is not attached to a facility.`);
      }
      if (orgStats.hqAssigned > 0) {
        reasons.push(`${orgStats.hqAssigned} headquarters learner(s) are already assigned this course.`);
      }
      return {
        tone: orgStats.rolloutExceptions > 0 ? 'amber' : 'blue',
        reasons,
        action: orgStats.facilityRolloutCount > 0 ? 'Review facility learner activity below to confirm facilities have started assigning it.' : 'Release it to the facilities that should carry this course.',
      };
    }

    return {
      tone: stats.total > 0 ? 'emerald' : 'blue',
      reasons: stats.total > 0
        ? [`${stats.total} learner(s) in your facility are already assigned this course.`]
        : ['This course is ready to assign inside your facility scope.'],
      action: stats.total > 0 ? 'Use Assignment Status below if you need to follow up with specific learners.' : 'Assign it to the team members who need it next.',
    };
  };

  const buildRiskGuidance = (item) => {
    switch (item.riskType) {
      case 'overdue_enrollment':
        return {
          reasons: [
            `${item.userName || 'This learner'} is past due for ${item.courseTitle || 'this course'}.`,
            item.facilityNames?.length
              ? `The work belongs to ${item.facilityNames.join(', ')}.`
              : 'This item is still not tied to a facility, so follow-up may require profile cleanup.',
          ],
          action: isOrgLevelTrainingManager
            ? 'Use this as oversight only and let the facility team handle the learner follow-up.'
            : item.actions?.includes('send_reminder')
              ? isFacilityManager
                ? 'Send a reminder first, then flag it for facility-admin review only if the learner stays stuck.'
                : 'Send a reminder first, then review the learner record if they stay stuck.'
              : 'Review the learner record and resolve the blocker.',
          tone: item.severity === 'critical' ? 'rose' : 'amber',
        };
      case 'blocked_learning_path':
        return {
          reasons: [
            `${item.userName || 'This learner'} cannot move forward in ${item.learningPathTitle || 'this learning path'}.`,
            item.blockedReason || 'An earlier required step still needs to be completed or released.',
          ],
          action: isOrgLevelTrainingManager
            ? 'Watch for repeated blockage patterns and intervene only if the facility team cannot clear the dependency.'
            : item.actions?.includes('remove_from_path')
              ? 'Use remove from path only if the learner should leave the program; otherwise clear the blocked step.'
              : isFacilityManager
                ? 'Flag this for facility-admin review if the path decision is beyond your authority.'
                : 'Check the blocked step before changing the learner assignment.',
          tone: 'amber',
        };
      case 'certificate_expiring':
      case 'certificate_expired':
        return {
          reasons: [
            `${item.userName || 'This learner'} has a credential that is ${item.riskType === 'certificate_expired' ? 'already expired' : 'approaching expiration'}.`,
            `${item.learningPathTitle || item.courseTitle || 'This credential'} may require renewal or reissue.`,
          ],
          action: isOrgLevelTrainingManager
            ? 'Use this to see which facility or headquarters program needs renewal attention.'
            : item.actions?.includes('reissue_certificate')
              ? 'Reissue only when the evidence is still valid; otherwise assign the renewal training.'
              : isFacilityManager
                ? 'Flag this for facility-admin review if certificate action is needed.'
                : 'Review certificate status and decide if the learner needs renewal work.',
          tone: item.riskType === 'certificate_expired' ? 'rose' : 'amber',
        };
      default:
        return {
          reasons: ['This queue item needs follow-up to keep training on track.'],
          action: isOrgLevelTrainingManager
            ? 'Use this as a monitoring signal and let the operating team work the item.'
            : isFacilityManager
              ? 'Flag this for facility-admin review if you cannot resolve it directly.'
              : 'Use the available action on the right instead of leaving the page.',
          tone: 'slate',
        };
    }
  };

  const buildDeadlineGuidance = (request) => ({
    tone: request.status === 'pending' ? 'amber' : request.status === 'approved' ? 'emerald' : 'rose',
    reasons: [
      request.scopeType === 'learning_path'
        ? 'This request changes the learning path deadline, not just the one course due date.'
        : 'This request affects only the course due date.',
      request.facilityNames?.length
        ? `This request belongs to ${request.facilityNames.join(', ')}.`
        : 'This request is still not tied to a facility, so learner mapping should be checked.',
    ],
    action: isOrgLevelTrainingManager
      ? request.status === 'pending'
        ? 'Use this as oversight only; facility admins and managers handle the actual approval decision.'
        : request.status === 'approved'
          ? 'The facility team has already handled this request.'
          : 'This remains part of the audit trail so headquarters can understand deadline pressure.'
      : request.status === 'pending'
        ? 'Approve only if the new date is still reasonable for the learner and the facility.'
        : request.status === 'approved'
          ? 'No further action is needed unless the learner falls behind again.'
          : 'The learner can submit a new request later if circumstances change.',
  });

  const getAverageScore = (courseId) => {
    const courseAssignments = assignments.filter((a) => a.courseId._id === courseId && a.scores?.overall);
    if (courseAssignments.length === 0) return null;
    const avg = courseAssignments.reduce((sum, a) => sum + (a.scores?.overall || 0), 0) / courseAssignments.length;
    return Math.round(avg);
  };
  const getAssignmentPathStatus = (course) => course?.assignmentPath || { inaccessiblePrerequisites: [], plannedPrerequisites: [] };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'not_started':
        return 'bg-gray-100 text-gray-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} />;
      case 'in_progress':
        return <Clock size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const riskTypeOptions = useMemo(() => {
    const values = [...new Set(visibleRiskQueue.map((item) => item.riskType).filter(Boolean))];
    return values.sort();
  }, [visibleRiskQueue]);

  const riskTypeLabel = (value) => String(value || '').replace(/_/g, ' ');
  const selectedRiskItems = filteredRiskQueue.filter((item) => selectedRiskKeys.includes(item.key));
  const allSelectedSupport = (action) =>
    selectedRiskItems.length > 0 && selectedRiskItems.every((item) => (item.actions || []).includes(action));
  const toggleRiskSelection = (riskKey) => {
    setSelectedRiskKeys((current) =>
      current.includes(riskKey) ? current.filter((key) => key !== riskKey) : [...current, riskKey],
    );
  };
  const toggleAllVisibleRiskSelections = () => {
    const visibleKeys = pagedRiskQueue.items.map((item) => item.key);
    const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selectedRiskKeys.includes(key));
    setSelectedRiskKeys((current) =>
      allVisibleSelected
        ? current.filter((key) => !visibleKeys.includes(key))
        : [...new Set([...current, ...visibleKeys])],
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Training Management</h1>
            <p className="text-gray-500 text-sm mt-1">Assign and track training for your facility team members</p>
          </div>
          {user && (user.role === 'facility_admin' || user.role === 'facility_manager') && (
            <button
              onClick={runReminders}
              className="ml-4 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium"
            >
              Send Reminders
            </button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {managementNarrative && (
          <div className="mb-6">
            <GuidancePanel
              title={managementNarrative.title}
              reasons={managementNarrative.reasons}
              action={managementNarrative.action}
              tone={managementNarrative.tone}
            />
          </div>
        )}

        {isOrgLevelTrainingManager && orgExecutiveSummary && (
          <div className="mb-8 space-y-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Executive Summary</h2>
              <p className="text-gray-500 text-sm">
                High-level training health for headquarters and released facilities.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard title="Assignments" value={orgExecutiveSummary.totalAssignments} tone="slate" />
              <SummaryCard title="Completion Rate" value={`${orgExecutiveSummary.completionRate}%`} tone="emerald" />
              <SummaryCard title="Overdue" value={orgExecutiveSummary.overdueAssignments} tone="amber" />
              <SummaryCard title="Blocked Paths" value={orgExecutiveSummary.blockedPaths} tone="rose" />
              <SummaryCard title="Certificate Risk" value={orgExecutiveSummary.certificateRisk} tone="blue" />
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Facility Compliance Heatmap</h3>
                <p className="text-sm text-gray-500">
                  Compare facility completion, overdue load, pending requests, and active risks in one place.
                </p>
              </div>
              {facilityHeatmapRows.length === 0 ? (
                <p className="text-sm text-gray-500">No facility activity yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="py-2 pr-4">Facility</th>
                        <th className="py-2 pr-4">Assigned</th>
                        <th className="py-2 pr-4">Completion</th>
                        <th className="py-2 pr-4">Overdue</th>
                        <th className="py-2 pr-4">Pending Requests</th>
                        <th className="py-2">Active Risks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facilityHeatmapRows.map((row) => (
                        <tr key={row.id} className="border-b border-gray-100">
                          <td className="py-3 pr-4 font-medium text-gray-900">{row.name}</td>
                          <td className="py-3 pr-4 text-gray-700">{row.assigned}</td>
                          <td className="py-3 pr-4">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              row.completionRate >= 80
                                ? 'bg-emerald-100 text-emerald-700'
                                : row.completionRate >= 50
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-rose-100 text-rose-700'
                            }`}>
                              {row.completionRate}%
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-gray-700">{row.overdue}</td>
                          <td className="py-3 pr-4 text-gray-700">{row.pendingRequests}</td>
                          <td className="py-3 text-gray-700">{row.activeRisks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Available Courses Section */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Assign Training</h2>
            <p className="text-gray-500 text-sm">
              {isOrgLevelTrainingManager
                ? 'Roll out courses to facilities or assign directly to headquarters team'
                : 'Select a course to assign to your facility team members'}
            </p>
          </div>

          {/* Search and Quick Assign */}
          <div className="space-y-4 mb-6">
            <label htmlFor="course-search-mgmt" className="sr-only">Search courses</label>
            <input
              id="course-search-mgmt"
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900 mb-1">Quick Assign</label>
                <Select
                  options={courses.map((course) => ({
                    value: course._id,
                    label: course.title,
                  }))}
                  isClearable
                  isSearchable
                  placeholder={isOrgLevelTrainingManager ? 'Select a course to assign to headquarters...' : 'Select a course to assign...'}
                  onChange={(option) => {
                    if (option) {
                      const course = courses.find((c) => c._id === option.value);
                      handleOpenAssignModal(course);
                    }
                  }}
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#9ca3af' },
                    }),
                  }}
                />
              </div>
            </div>

            {isOrgLevelTrainingManager && facilityFilterOptions.length > 0 && (
              <div className="max-w-md">
                <label htmlFor="facility-filter" className="block text-sm font-medium text-gray-900 mb-1">
                  Facility View
                </label>
                <select
                  id="facility-filter"
                  value={selectedFacilityFilter}
                  onChange={(e) => setSelectedFacilityFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Facilities</option>
                  {facilityFilterOptions.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Desktop Grid View */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full flex items-center justify-center h-64" role="status" aria-label="Loading courses">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="col-span-full bg-white rounded-lg p-8 text-center">
                <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                <p className="text-gray-500 font-medium">No courses available</p>
              </div>
            ) : (
              pagedCourses.items.map((course) => {
                const stats = getEnrollmentStats(course._id);
                const orgStats = getOrgCourseStats(course._id);
                const assignmentPath = getAssignmentPathStatus(course);
                const missingAccess = assignmentPath.inaccessiblePrerequisites || [];
                const plannedSteps = assignmentPath.plannedPrerequisites || [];
                const assignmentBlocked = missingAccess.length > 0;
                const courseGuidance = buildCourseGuidance(course, orgStats, stats, assignmentBlocked, missingAccess, plannedSteps);
                return (
                  <div key={course._id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-grow">{course.shortDescription}</p>
                    {assignmentBlocked && (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Missing prerequisite access: {missingAccess.map((item) => item.title).join(', ')}
                      </div>
                    )}
                    {!assignmentBlocked && plannedSteps.length > 0 && (
                      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        Planned path steps not live yet: {plannedSteps.map((item) => item.title).join(', ')}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="space-y-2 mb-4 py-3 border-y border-gray-100">
                      {isOrgLevelTrainingManager ? (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">Facilities Active</span>
                            <span className="font-medium text-gray-900">{orgStats.facilityRolloutCount}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">HQ Assigned</span>
                            <span className="font-medium text-gray-900">{orgStats.hqAssigned}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">HQ Completed</span>
                            <span className="font-medium text-green-600">{orgStats.hqCompleted}</span>
                          </div>
                          {orgStats.rolloutExceptions > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-amber-700">Unmapped Facility Learners (This Course)</span>
                              <span className="font-medium text-amber-700">{orgStats.rolloutExceptions}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">Assigned</span>
                            <span className="font-medium text-gray-900">{stats.total}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">Completed</span>
                            <span className="font-medium text-green-600">{stats.completed}</span>
                          </div>
                        </>
                      )}
                      {!isOrgLevelTrainingManager && stats.completed > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Avg Score</span>
                          <span className="font-medium text-gray-900">{getAverageScore(course._id)}%</span>
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <GuidancePanel
                        title="Why this is showing"
                        reasons={courseGuidance.reasons}
                        action={courseGuidance.action}
                        tone={courseGuidance.tone}
                      />
                    </div>

                    {isOrgLevelTrainingManager ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => handleOpenRolloutModal(course)}
                          disabled={assignmentBlocked}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                        >
                          <Building2 size={16} aria-hidden="true" />
                          {assignmentBlocked ? 'Path not ready' : 'Roll Out to Facilities'}
                        </button>
                        <button
                          onClick={() => handleOpenAssignModal(course)}
                          disabled={assignmentBlocked}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                        >
                          <Plus size={16} aria-hidden="true" />
                          {assignmentBlocked ? 'Path not ready' : 'Assign to HQ'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleOpenAssignModal(course)}
                        disabled={assignmentBlocked}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                      >
                        <Plus size={16} aria-hidden="true" />
                        {assignmentBlocked ? 'Path not ready' : 'Assign'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {loading ? (
              <div className="flex items-center justify-center h-64" role="status" aria-label="Loading courses">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="bg-white rounded-lg p-6 text-center">
                <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                <p className="text-gray-500 font-medium">No courses available</p>
              </div>
            ) : (
              pagedCourses.items.map((course) => {
                const stats = getEnrollmentStats(course._id);
                const orgStats = getOrgCourseStats(course._id);
                const assignmentPath = getAssignmentPathStatus(course);
                const missingAccess = assignmentPath.inaccessiblePrerequisites || [];
                const plannedSteps = assignmentPath.plannedPrerequisites || [];
                const assignmentBlocked = missingAccess.length > 0;
                const courseGuidance = buildCourseGuidance(course, orgStats, stats, assignmentBlocked, missingAccess, plannedSteps);
                return (
                  <div key={course._id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.shortDescription}</p>
                    {assignmentBlocked && (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Missing prerequisite access: {missingAccess.map((item) => item.title).join(', ')}
                      </div>
                    )}
                    {!assignmentBlocked && plannedSteps.length > 0 && (
                      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        Planned path steps not live yet: {plannedSteps.map((item) => item.title).join(', ')}
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2 mb-4 py-3 border-y border-gray-100">
                      {isOrgLevelTrainingManager ? (
                        <>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">Facilities</div>
                            <div className="font-semibold text-gray-900">{orgStats.facilityRolloutCount}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">HQ Assigned</div>
                            <div className="font-semibold text-gray-900">{orgStats.hqAssigned}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">HQ Done</div>
                            <div className="font-semibold text-green-600">{orgStats.hqCompleted}</div>
                          </div>
                          {orgStats.rolloutExceptions > 0 && (
                            <div className="text-center">
                              <div className="text-xs text-amber-700 mb-1">Needs Mapping</div>
                              <div className="font-semibold text-amber-700">{orgStats.rolloutExceptions}</div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">Assigned</div>
                            <div className="font-semibold text-gray-900">{stats.total}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">Completed</div>
                            <div className="font-semibold text-green-600">{stats.completed}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">Avg Score</div>
                            <div className="font-semibold text-gray-900">{getAverageScore(course._id) || '-'}%</div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="mb-4">
                      <GuidancePanel
                        title="Why this is showing"
                        reasons={courseGuidance.reasons}
                        action={courseGuidance.action}
                        tone={courseGuidance.tone}
                      />
                    </div>

                    {isOrgLevelTrainingManager ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => handleOpenRolloutModal(course)}
                          disabled={assignmentBlocked}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                        >
                          <Building2 size={16} aria-hidden="true" />
                          {assignmentBlocked ? 'Path not ready' : 'Roll Out'}
                        </button>
                        <button
                          onClick={() => handleOpenAssignModal(course)}
                          disabled={assignmentBlocked}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                        >
                          <Plus size={16} aria-hidden="true" />
                          {assignmentBlocked ? 'Path not ready' : 'Assign HQ'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleOpenAssignModal(course)}
                        disabled={assignmentBlocked}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-600 font-medium text-sm"
                      >
                        <Plus size={16} aria-hidden="true" />
                        {assignmentBlocked ? 'Path not ready' : 'Assign to Team'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <SectionPagination
            page={pagedCourses.page}
            totalPages={pagedCourses.totalPages}
            totalItems={pagedCourses.totalItems}
            itemLabel="courses"
            onPageChange={setCoursePage}
          />
        </div>

        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <GitBranch size={22} className="text-primary-600" />
              Learning Paths
            </h2>
            <p className="text-gray-500 text-sm">
              {isOrgLevelTrainingManager
                ? 'Track path rollout across headquarters and facility audiences'
                : 'Track multi-course programs assigned inside your facility scope'}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
              </div>
            ) : visibleLearningPaths.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                <p className="text-gray-500 font-medium">No learning paths assigned yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full" aria-label="Learning path rollout">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                    {isOrgLevelTrainingManager && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HQ / Facility</th>
                    )}
                    {!isOrgLevelTrainingManager && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facilities Active</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blocked</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overdue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pagedLearningPaths.items.map((path) => (
                    <tr key={path.learningPathId}>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900 text-sm">{path.title}</p>
                        <p className="text-xs text-gray-500">{String(path.audienceScope || 'mixed').replace(/_/g, ' ')}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{String(path.mode || '').replace(/_/g, ' ')}</td>
                      {isOrgLevelTrainingManager ? (
                        <td className="px-6 py-4 text-sm text-gray-700">{path.hqAssigned || 0} / {path.facilityAssigned || 0}</td>
                      ) : (
                        <td className="px-6 py-4 text-sm text-gray-700">{path.facilityCount || 0}</td>
                      )}
                      <td className="px-6 py-4 text-sm text-gray-700">{path.assignedLearners || 0}</td>
                      <td className="px-6 py-4 text-sm text-amber-700">{path.blockedLearners || 0}</td>
                      <td className="px-6 py-4 text-sm text-rose-700">{path.overdueLearners || 0}</td>
                      <td className="px-6 py-4">
                        <div className="w-32 bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={path.avgProgress || 0} aria-valuemin={0} aria-valuemax={100} aria-label={`${path.title} path progress`}>
                          <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${path.avgProgress || 0}%` }} />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{path.avgProgress || 0}%</p>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleOpenAssignPathModal(path)}
                          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-xs font-medium text-white hover:bg-primary-700"
                        >
                          <Plus size={14} aria-hidden="true" />
                          {isOrgLevelTrainingManager ? 'Assign HQ' : 'Assign Path'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
          <SectionPagination
            page={pagedLearningPaths.page}
            totalPages={pagedLearningPaths.totalPages}
            totalItems={pagedLearningPaths.totalItems}
            itemLabel="learning paths"
            onPageChange={setPathPage}
          />
        </div>

        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Action Queue</h2>
            <p className="text-gray-500 text-sm">
              {isOrgLevelTrainingManager
                ? 'Monitor risk and intervention workload across headquarters and facilities.'
                : 'Act on learner risk items inside your facility scope.'}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
              </div>
            ) : filteredRiskQueue.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                <p className="text-gray-500 font-medium">No intervention items match the current filters</p>
              </div>
            ) : (
              <>
                <div className="border-b border-gray-100 px-6 py-4 space-y-4">
                  <div className="grid gap-3 md:grid-cols-5">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Active</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{visibleRiskQueue.length}</p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">High Priority</p>
                      <p className="mt-1 text-2xl font-bold text-amber-900">{visibleRiskQueue.filter((item) => item.severity === 'high').length}</p>
                    </div>
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Critical</p>
                      <p className="mt-1 text-2xl font-bold text-red-900">{visibleRiskQueue.filter((item) => item.severity === 'critical').length}</p>
                    </div>
                    <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">Flagged For Admin Review</p>
                      <p className="mt-1 text-2xl font-bold text-violet-900">{visibleRiskQueue.filter((item) => item.isEscalated).length}</p>
                    </div>
                    <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Filtered</p>
                      <p className="mt-1 text-2xl font-bold text-sky-900">{filteredRiskQueue.length}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
                    <select
                      value={riskTypeFilter}
                      onChange={(event) => {
                        setRiskTypeFilter(event.target.value);
                        setRiskPage(1);
                        setSelectedRiskKeys([]);
                      }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none"
                    >
                      <option value="all">All Risk Types</option>
                      {riskTypeOptions.map((type) => (
                        <option key={type} value={type}>{riskTypeLabel(type)}</option>
                      ))}
                    </select>
                    <select
                      value={riskSeverityFilter}
                      onChange={(event) => {
                        setRiskSeverityFilter(event.target.value);
                        setRiskPage(1);
                        setSelectedRiskKeys([]);
                      }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none"
                    >
                      <option value="all">All Severity</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <select
                      value={riskStatusFilter}
                      onChange={(event) => {
                        setRiskStatusFilter(event.target.value);
                        setRiskPage(1);
                        setSelectedRiskKeys([]);
                      }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none"
                    >
                      <option value="all">All Queue Status</option>
                      <option value="active">Open Only</option>
                      <option value="escalated">Flagged For Admin Review</option>
                    </select>
                    {!isOrgLevelTrainingManager && (
                      <button
                        type="button"
                        onClick={toggleAllVisibleRiskSelections}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {pagedRiskQueue.items.length > 0 && pagedRiskQueue.items.every((item) => selectedRiskKeys.includes(item.key))
                          ? 'Clear Page Selection'
                          : 'Select Page'}
                      </button>
                    )}
                  </div>

                  {!isOrgLevelTrainingManager && selectedRiskItems.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <p className="text-sm font-medium text-gray-800">
                          {selectedRiskItems.length} queue item(s) selected
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {allSelectedSupport('mark_resolved') && (
                            <button
                              type="button"
                              onClick={() => handleBulkRiskQueueAction('mark_resolved')}
                              className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Mark resolved
                            </button>
                          )}
                          {isFacilityManager && allSelectedSupport('escalate') && (
                            <button
                              type="button"
                              onClick={() => handleBulkRiskQueueAction('escalate')}
                              className="inline-flex items-center rounded-lg border border-purple-200 px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50"
                            >
                              Flag for Facility Admin Review
                            </button>
                          )}
                          {allSelectedSupport('remove_from_path') && (
                            <button
                              type="button"
                              onClick={() => handleBulkRiskQueueAction('remove_from_path')}
                              className="inline-flex items-center rounded-lg border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50"
                            >
                              Remove from path
                            </button>
                          )}
                          {allSelectedSupport('reissue_certificate') && (
                            <button
                              type="button"
                              onClick={() => handleBulkRiskQueueAction('reissue_certificate')}
                              className="inline-flex items-center rounded-lg border border-sky-200 px-3 py-2 text-xs font-medium text-sky-700 hover:bg-sky-50"
                            >
                              Reissue certificates
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="divide-y divide-gray-200">
                {pagedRiskQueue.items.map((item) => {
                  const riskGuidance = buildRiskGuidance(item);
                  return (
                  <div key={item.key} className="px-6 py-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {!isOrgLevelTrainingManager && (
                            <input
                              type="checkbox"
                              checked={selectedRiskKeys.includes(item.key)}
                              onChange={() => toggleRiskSelection(item.key)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          )}
                          <p className="font-medium text-gray-900 text-sm">{item.userName || item.learningPathTitle || 'Risk item'}</p>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            item.severity === 'critical'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {String(item.riskType || '').replace(/_/g, ' ')}
                          </span>
                          {item.isEscalated && (
                            <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700">
                              flagged for admin review
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {item.learningPathTitle || item.courseTitle || 'Operational risk item'}
                        </p>
                        <div className="grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
                          <p><span className="font-medium text-gray-800">Facility:</span> {item.facilityNames?.length ? item.facilityNames.join(', ') : 'Headquarters'}</p>
                          <p><span className="font-medium text-gray-800">Due / risk date:</span> {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : 'N/A'}</p>
                        </div>
                        {item.blockedReason && (
                          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                            {item.blockedReason}
                          </p>
                        )}
                        <GuidancePanel
                          title="Why this needs attention"
                          reasons={riskGuidance.reasons}
                          action={riskGuidance.action}
                          tone={riskGuidance.tone}
                        />
                        {item.interventionHistory?.length > 0 && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Intervention history</p>
                            <div className="mt-2 space-y-1.5">
                              {item.interventionHistory.slice(0, 2).map((entry, index) => (
                                <p key={`${item.key}-history-${index}`} className="text-xs text-gray-600">
                                  <span className="font-medium text-gray-800">{String(entry.actionType || '').replace(/_/g, ' ')}</span>
                                  {entry.note ? ` · ${entry.note}` : ''}
                                  {entry.escalationTarget ? ` · flagged for ${entry.escalationTarget.replace(/_/g, ' ')}` : ''}
                                  {entry.actorName ? ` · ${entry.actorName}` : ''}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {!isOrgLevelTrainingManager && item.actions?.includes('send_reminder') && (
                          <button
                            onClick={runReminders}
                            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Send reminder
                          </button>
                        )}
                        {item.riskType === 'deadline_request_pending' && !isOrgLevelTrainingManager && (
                          <>
                            <button
                              onClick={() => handleDeadlineRequestResponse({ enrollmentId: item.enrollmentId, requestId: item.requestId }, 'approve')}
                              className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeadlineRequestResponse({ enrollmentId: item.enrollmentId, requestId: item.requestId }, 'deny')}
                              className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
                            >
                              Deny
                            </button>
                          </>
                        )}
                        {!isOrgLevelTrainingManager && item.actions?.includes('remove_from_path') && item.learningPathEnrollmentId && (
                          <button
                            onClick={() => handleRiskQueueAction(item, 'remove_from_path')}
                            className="inline-flex items-center rounded-lg border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50"
                          >
                            Remove from path
                          </button>
                        )}
                        {!isOrgLevelTrainingManager && item.actions?.includes('reissue_certificate') && item.certificateId && (
                          <button
                            onClick={() => handleRiskQueueAction(item, 'reissue_certificate')}
                            className="inline-flex items-center rounded-lg border border-sky-200 px-3 py-2 text-xs font-medium text-sky-700 hover:bg-sky-50"
                          >
                            Reissue certificate
                          </button>
                        )}
                        {!isOrgLevelTrainingManager && item.actions?.includes('mark_resolved') && (
                          <button
                            onClick={() => handleRiskQueueAction(item, 'mark_resolved')}
                            className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Mark resolved
                          </button>
                        )}
                        {!isOrgLevelTrainingManager && isFacilityManager && item.actions?.includes('escalate') && (
                          <button
                            onClick={() => handleRiskQueueAction(item, 'escalate')}
                            className="inline-flex items-center rounded-lg border border-purple-200 px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-50"
                          >
                            Flag for Facility Admin Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )})}
                </div>
                <SectionPagination
                  page={pagedRiskQueue.page}
                  totalPages={pagedRiskQueue.totalPages}
                  totalItems={pagedRiskQueue.totalItems}
                  itemLabel="risk items"
                  onPageChange={setRiskPage}
                />
              </>
            )}
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-4">
            <h2 id="deadline-requests" className="scroll-mt-24 text-xl sm:text-2xl font-bold text-gray-900 mb-2">Deadline Requests</h2>
            <p className="text-gray-500 text-sm">
              {isOrgLevelTrainingManager
                ? 'Monitor facility deadline-request volume without stepping into facility-level approval work.'
                : 'Review learner extension requests and approve or deny them without leaving training management.'}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
              </div>
            ) : visibleDeadlineRequests.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                <p className="text-gray-500 font-medium">
                  {selectedFacilityFilter === 'all' ? 'No deadline requests yet' : 'No deadline requests for the selected facility'}
                </p>
              </div>
            ) : isOrgLevelTrainingManager ? (
              <div className="space-y-6 px-6 py-6">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="text-xs text-gray-500 mb-1">Total Requests</div>
                    <div className="text-lg font-semibold text-gray-900">{visibleDeadlineRequests.length}</div>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="text-xs text-amber-700 mb-1">Pending</div>
                    <div className="text-lg font-semibold text-amber-800">
                      {visibleDeadlineRequests.filter((request) => request.status === 'pending').length}
                    </div>
                  </div>
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <div className="text-xs text-green-700 mb-1">Approved</div>
                    <div className="text-lg font-semibold text-green-800">
                      {visibleDeadlineRequests.filter((request) => request.status === 'approved').length}
                    </div>
                  </div>
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                    <div className="text-xs text-red-700 mb-1">Denied</div>
                    <div className="text-lg font-semibold text-red-800">
                      {visibleDeadlineRequests.filter((request) => request.status === 'denied').length}
                    </div>
                  </div>
                  <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3">
                    <div className="text-xs text-sky-700 mb-1">Path Scoped</div>
                    <div className="text-lg font-semibold text-sky-800">
                      {visibleDeadlineRequests.filter((request) => request.scopeType === 'learning_path').length}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                  <p className="text-sm font-medium text-gray-900">Facility-level approvals stay with facility admins and managers.</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Headquarters can monitor request volume here, while Stratel admin and facility teams handle the actual approval workflow in their operational queues.
                  </p>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full" aria-label="Deadline request oversight">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Learner</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course / Path</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Due</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {pagedDeadlineRequests.items.map((request) => {
                        const deadlineGuidance = buildDeadlineGuidance(request);
                        return (
                          <tr key={request.requestId}>
                            <td className="px-6 py-4 text-sm text-gray-900">{request.userName}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {request.scopeType === 'learning_path' ? 'Path deadline' : 'Course deadline'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {request.courseTitle}
                              {request.pathTitle ? ` · ${request.pathTitle}` : ''}
                              <div className="mt-2 max-w-sm">
                                <GuidancePanel
                                  reasons={deadlineGuidance.reasons}
                                  action={deadlineGuidance.action}
                                  tone={deadlineGuidance.tone}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {request.requestedDueDate ? new Date(request.requestedDueDate).toLocaleString() : 'No requested date'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {request.facilityNames?.length ? request.facilityNames.join(', ') : 'Facility mapping still needed'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                                request.status === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : request.status === 'denied'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-amber-100 text-amber-700'
                              }`}>
                                {request.status.replace(/_/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                      {pagedDeadlineRequests.items.map((request) => {
                  const deadlineGuidance = buildDeadlineGuidance(request);
                  return (
                  <div key={request.requestId} className="px-6 py-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-gray-900 text-sm">{request.userName}</p>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            request.status === 'approved'
                              ? 'bg-green-100 text-green-700'
                              : request.status === 'denied'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {request.status.replace(/_/g, ' ')}
                          </span>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            request.scopeType === 'learning_path'
                              ? 'bg-sky-100 text-sky-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {request.scopeType === 'learning_path' ? 'Path deadline' : 'Course deadline'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {request.courseTitle}
                          {request.pathTitle ? ` · ${request.pathTitle}` : ''}
                        </p>
                        <div className="grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
                          <p><span className="font-medium text-gray-800">Current due:</span> {request.currentDueDate ? new Date(request.currentDueDate).toLocaleString() : 'No due date'}</p>
                          <p><span className="font-medium text-gray-800">Requested due:</span> {request.requestedDueDate ? new Date(request.requestedDueDate).toLocaleString() : 'No requested date'}</p>
                          <p><span className="font-medium text-gray-800">Requested:</span> {request.requestedAt ? new Date(request.requestedAt).toLocaleString() : 'Unknown'}</p>
                          <p><span className="font-medium text-gray-800">Facility:</span> {request.facilityNames?.length ? request.facilityNames.join(', ') : 'Facility mapping still needed'}</p>
                        </div>
                        {request.requestedComment && (
                          <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            {request.requestedComment}
                          </p>
                        )}
                        <GuidancePanel
                          title="Why this request is here"
                          reasons={deadlineGuidance.reasons}
                          action={deadlineGuidance.action}
                          tone={deadlineGuidance.tone}
                        />
                        {request.status !== 'pending' && (
                          <p className="text-xs text-gray-500">
                            {request.respondedBy ? `Handled by ${request.respondedBy}` : 'Handled'}
                            {request.respondedAt ? ` on ${new Date(request.respondedAt).toLocaleString()}` : ''}
                            {request.response ? ` · ${request.response}` : ''}
                          </p>
                        )}
                      </div>

                      {request.status === 'pending' && (
                        <div className="flex shrink-0 gap-2">
                          <button
                            onClick={() => handleDeadlineRequestResponse(request, 'approve')}
                            className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDeadlineRequestResponse(request, 'deny')}
                            className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                          >
                            Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )})}
              </div>
            )}
          </div>
          <SectionPagination
            page={pagedDeadlineRequests.page}
            totalPages={pagedDeadlineRequests.totalPages}
            totalItems={pagedDeadlineRequests.totalItems}
            itemLabel="requests"
            onPageChange={setDeadlinePage}
          />
        </div>

        {/* Assignment Status Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {isOrgLevelTrainingManager ? 'Rollout Status' : 'Assignment Status'}
            </h2>
            <p className="text-gray-500 text-sm">
              {isOrgLevelTrainingManager
                ? 'Track facility rollout separately from headquarters workforce training'
                : 'Track completion and progress across your team'}
            </p>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <label htmlFor="filter-course" className="sr-only">Filter by course</label>
            <select
              id="filter-course"
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Courses</option>
              {assignments
                .map((a) => a.courseId)
                .filter((v, i, a) => a.findIndex((t) => t._id === v._id) === i)
                .map((course) => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
            </select>

            <label htmlFor="filter-status" className="sr-only">Filter by status</label>
            <select
              id="filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {isOrgLevelTrainingManager ? (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Layers3 size={18} className="text-primary-600" />
                    Package Visibility
                  </h3>
                  <p className="text-sm text-gray-500">Operational bundle visibility for this client, without authoring or credential governance controls.</p>
                </div>
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <SummaryCard title="Packages" value={orgPackageVisibilitySummary.visiblePackages.length} tone="slate" />
                    <SummaryCard title="Assigned Learners" value={orgPackageVisibilitySummary.assignedLearners} tone="blue" />
                    <SummaryCard title="Completed" value={orgPackageVisibilitySummary.completedLearners} tone="emerald" />
                    <SummaryCard title="Avg. Progress" value={`${orgPackageVisibilitySummary.averageProgress}%`} tone="amber" />
                  </div>
                </div>
                <div className="px-6 py-4">
                  {loading ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                    </div>
                  ) : orgPackageVisibilitySummary.visiblePackages.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-sm font-medium text-gray-500">No package rollout activity is visible for the current scope.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {orgPackageVisibilitySummary.visiblePackages.slice(0, 4).map((item) => (
                        <div key={item.learningPackageAssetId || item.learningPackageId} className="rounded-lg border border-gray-200 px-4 py-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                              <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{labelize(item.packageType)}</p>
                              <p className="mt-2 text-sm text-gray-600">
                                {item.facilityNames?.length ? item.facilityNames.join(', ') : 'Headquarters or mixed facility scope'}
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 md:min-w-[260px]">
                              <div><span className="font-medium text-gray-900">Learners:</span> {item.assignedLearners || 0}</div>
                              <div><span className="font-medium text-gray-900">Completed:</span> {item.completedLearners || 0}</div>
                              <div><span className="font-medium text-gray-900">Overdue:</span> {item.overdueLearners || 0}</div>
                              <div><span className="font-medium text-gray-900">Progress:</span> {item.avgProgress || 0}%</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!loading && orgPackageVisibilitySummary.overdueLearners > 0 && (
                  <div className="px-6 pb-4">
                    <GuidancePanel
                      title="Package rollout watch"
                      reasons={[
                        `${orgPackageVisibilitySummary.overdueLearners} package learner assignments are overdue.`,
                        'Use the risk queue and facility learner activity tables below to target follow-up.',
                      ]}
                      tone="amber"
                    />
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Layers3 size={18} className="text-primary-600" />
                    Released To Facilities
                  </h3>
                  <p className="text-sm text-gray-500">This shows where headquarters has released courses to facilities, even before any facility learners are assigned.</p>
                </div>
                <div className="px-6 py-4 border-b border-gray-100">
                  <GuidancePanel
                    title="How to read this"
                    reasons={[
                      'A release means headquarters has made the course available to a facility.',
                      'It does not mean the facility has already assigned it to individual learners.',
                    ]}
                    action="Use Facility Learner Activity below to confirm that released courses are being used."
                    tone="blue"
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                  </div>
                ) : rolloutPlanSummary.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                    <p className="text-gray-500 font-medium">No rollout plans saved yet</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                    <table className="w-full" aria-label="Facility rollout plan">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facilities</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {pagedRolloutPlans.items.map((plan) => (
                          <tr key={plan.key}>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{plan.courseTitle}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {plan.facilityNames.slice(0, 3).join(', ')}
                              {plan.facilityCount > 3 ? ` +${plan.facilityCount - 3} more` : ''}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {plan.latestRolledOutAt ? new Date(plan.latestRolledOutAt).toLocaleString() : 'Unknown'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <SectionPagination
                      page={pagedRolloutPlans.page}
                      totalPages={pagedRolloutPlans.totalPages}
                      totalItems={pagedRolloutPlans.totalItems}
                      itemLabel="rollout plans"
                      onPageChange={setRolloutPlanPage}
                    />
                  </>
                )}
              </div>

              {rolloutExceptionAssignments.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {rolloutExceptionAssignments.length} facility-scoped assignment(s) are missing facility mapping across all listed courses. They are excluded from facility learner activity until the learner profile is attached to a facility.
                </div>
              )}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">Facility Learner Activity</h3>
                  <p className="text-sm text-gray-500">This shows actual learner assignments and progress inside released facilities. Facility admins still own this execution.</p>
                </div>
                <div className="px-6 py-4 border-b border-gray-100">
                  <GuidancePanel
                    title="How to read this"
                    reasons={[
                      'Only learners with valid facility mapping appear here.',
                      'If release exists but this table is empty, the facility may not have assigned the course yet or learner profiles still need mapping cleanup.',
                    ]}
                    action="If usage looks low, check unmapped learner warnings first before assuming the facility has ignored the rollout."
                    tone={rolloutExceptionAssignments.length > 0 ? 'amber' : 'slate'}
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                  </div>
                ) : facilityRolloutRows.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                    <p className="text-gray-500 font-medium">No learner assignments inside rolled out facilities yet</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                    <table className="w-full" aria-label="Facility rollout">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Learners</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">In Progress</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Not Started</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {pagedFacilityRolloutRows.items.map((row) => (
                          <tr key={row.key}>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.courseTitle}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{row.facilityName}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{row.assigned}</td>
                            <td className="px-6 py-4 text-sm text-blue-700">{row.inProgress}</td>
                            <td className="px-6 py-4 text-sm text-green-700">{row.completed}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{row.notStarted}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <SectionPagination
                      page={pagedFacilityRolloutRows.page}
                      totalPages={pagedFacilityRolloutRows.totalPages}
                      totalItems={pagedFacilityRolloutRows.totalItems}
                      itemLabel="rollout rows"
                      onPageChange={setRolloutRowPage}
                    />
                  </>
                )}
              </div>

              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">Headquarters Workforce</h3>
                  <p className="text-sm text-gray-500">Organization-level developmental, certification, and admin training stays separate from facility rollout.</p>
                </div>
                <div className="px-6 py-4 border-b border-gray-100">
                  <GuidancePanel
                    title="Why this is separate"
                    reasons={[
                      'Headquarters assignments are not mixed into facility execution metrics.',
                      'This keeps organization-level training distinct from facility workforce rollout.',
                    ]}
                    action="Use this section for HQ-only programs and use the facility sections above for operational rollout."
                    tone="slate"
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                  </div>
                ) : headquartersAssignments.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                    <p className="text-gray-500 font-medium">No headquarters assignments yet</p>
                  </div>
                ) : (
                  <>
                  <table className="w-full" aria-label="Headquarters assignments">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Headquarters Team Member</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {pagedHeadquartersAssignments.items.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4"><p className="font-medium text-gray-900 text-sm">{assignment.courseId.title}</p></td>
                          <td className="px-6 py-4"><p className="text-gray-900 text-sm">{assignment.userId.firstName} {assignment.userId.lastName}</p></td>
                          <td className="px-6 py-4">
                            <div className={`flex items-center gap-1 w-fit px-2 py-1 rounded text-xs font-medium ${getStatusColor(assignment.status)}`}>
                              <span aria-hidden="true">{getStatusIcon(assignment.status)}</span>
                              {assignment.status.replace(/_/g, ' ')}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="w-32 bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={assignment.progress?.percentage || 0} aria-valuemin={0} aria-valuemax={100} aria-label={`${assignment.courseId.title} progress`}>
                              <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${assignment.progress?.percentage || 0}%` }} />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{assignment.progress?.percentage || 0}%</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{assignment.scores?.overall ? `${assignment.scores.overall}%` : '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {assignment.status === 'not_started' ? (
                              <button onClick={() => handleRemoveAssignment(assignment)} className="text-red-600 hover:text-red-700 font-medium">Unassign</button>
                            ) : (
                              <span className="text-xs text-gray-400">Started</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <SectionPagination
                    page={pagedHeadquartersAssignments.page}
                    totalPages={pagedHeadquartersAssignments.totalPages}
                    totalItems={pagedHeadquartersAssignments.totalItems}
                    itemLabel="HQ assignments"
                    onPageChange={setHqAssignmentPage}
                  />
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-4">
                  <GuidancePanel
                    title="Keep this simple"
                    reasons={[
                      'This table is your facility-only learner view.',
                      'Use the action queue for urgent issues first, then come here for learner-by-learner follow-up.',
                    ]}
                    action="You do not need to work anything outside your facility from this page."
                    tone="blue"
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center h-64" role="status" aria-label="Loading assignments">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                  </div>
                ) : filteredAssignments.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                    <p className="text-gray-500 font-medium">No assignments yet</p>
                  </div>
                ) : (
                  <>
                  <table className="w-full" aria-label="Assignment status">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facility Team Member</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {pagedFacilityAssignments.items.map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4"><p className="font-medium text-gray-900 text-sm">{assignment.courseId.title}</p></td>
                          <td className="px-6 py-4"><p className="text-gray-900 text-sm">{assignment.userId.firstName} {assignment.userId.lastName}</p></td>
                          <td className="px-6 py-4">
                            <div className={`flex items-center gap-1 w-fit px-2 py-1 rounded text-xs font-medium ${getStatusColor(assignment.status)}`}>
                              <span aria-hidden="true">{getStatusIcon(assignment.status)}</span>
                              {assignment.status.replace(/_/g, ' ')}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="w-32 bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={assignment.progress?.percentage || 0} aria-valuemin={0} aria-valuemax={100} aria-label={`${assignment.courseId.title} progress`}>
                              <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${assignment.progress?.percentage || 0}%` }} />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{assignment.progress?.percentage || 0}%</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{assignment.scores?.overall ? `${assignment.scores.overall}%` : '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {assignment.status === 'not_started' ? (
                              <button onClick={() => handleRemoveAssignment(assignment)} className="text-red-600 hover:text-red-700 font-medium">Unassign</button>
                            ) : (
                              <span className="text-xs text-gray-400">Started</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <SectionPagination
                    page={pagedFacilityAssignments.page}
                    totalPages={pagedFacilityAssignments.totalPages}
                    totalItems={pagedFacilityAssignments.totalItems}
                    itemLabel="assignments"
                    onPageChange={setFacilityAssignmentPage}
                  />
                  </>
                )}
              </div>

              <div className="md:hidden space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center h-64" role="status" aria-label="Loading assignments">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
                  </div>
                ) : filteredAssignments.length === 0 ? (
                  <div className="bg-white rounded-lg p-6 text-center">
                    <AlertCircle className="mx-auto mb-4 text-gray-400" size={32} aria-hidden="true" />
                    <p className="text-gray-500 font-medium">No assignments yet</p>
                  </div>
                ) : (
                  pagedFacilityAssignments.items.map((assignment) => (
                    <div key={assignment._id} className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-900">{assignment.courseId.title}</h4>
                        <p className="text-sm text-gray-600">{assignment.userId.firstName} {assignment.userId.lastName}</p>
                      </div>
                      <div className="space-y-2 mb-3 py-3 border-y border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Status</span>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(assignment.status)}`}>
                            <span aria-hidden="true">{getStatusIcon(assignment.status)}</span>
                            {assignment.status.replace(/_/g, ' ')}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Progress</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-1.5" role="progressbar" aria-valuenow={assignment.progress?.percentage || 0} aria-valuemin={0} aria-valuemax={100} aria-label={`${assignment.courseId.title} progress`}>
                              <div className="bg-primary-600 h-1.5 rounded-full" style={{ width: `${assignment.progress?.percentage || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 w-10 text-right">{assignment.progress?.percentage || 0}%</span>
                          </div>
                        </div>
                        {assignment.scores?.overall && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Score</span>
                            <span className="text-sm font-medium text-gray-900">{assignment.scores.overall}%</span>
                          </div>
                        )}
                        {assignment.dueDate && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Due</span>
                            <span className="text-sm text-gray-600">{new Date(assignment.dueDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      <div className="pt-1">
                        {assignment.status === 'not_started' ? (
                          <button onClick={() => handleRemoveAssignment(assignment)} className="text-sm font-medium text-red-600 hover:text-red-700">
                            Unassign and release seat
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Seat consumed after start</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAssignModal && selectedCourse && (
        <AssignCourseModal
          course={selectedCourse}
          teamMembers={visibleAssignableTeamMembers}
          audienceLabel={isOrgLevelTrainingManager ? 'headquarters team members' : 'facility team members'}
          allowedRoleOptions={isOrgLevelTrainingManager ? ['org_admin', 'org_team'] : ['facility_admin', 'facility_manager', 'facility_worker']}
          onClose={handleCloseAssignModal}
        />
      )}
      {showRolloutModal && rolloutCourse && (
        <RolloutCourseModal
          course={rolloutCourse}
          organizationId={organizationId}
          onClose={handleCloseRolloutModal}
        />
      )}
      {showAssignPathModal && selectedLearningPath && (
        <AssignLearningPathModal
          learningPath={selectedLearningPath}
          teamMembers={visibleAssignableTeamMembers}
          audienceLabel={isOrgLevelTrainingManager ? 'headquarters team members' : 'facility team members'}
          allowedRoleOptions={isOrgLevelTrainingManager ? ['org_admin', 'org_team'] : ['facility_admin', 'facility_manager', 'facility_worker']}
          onClose={handleCloseAssignPathModal}
        />
      )}
    </div>
  );
}

function SectionPagination({ page, totalPages, totalItems, itemLabel, onPageChange }) {
  if (!totalItems || totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
      <p className="text-sm text-gray-500">
        Showing page {page} of {totalPages} · {totalItems} {itemLabel}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={14} />
          Prev
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
